import React, { useState } from 'react';
import { Calendar, Phone, ExternalLink, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import GuidedUpsell from '../../components/GuidedUpsell';
import { useAuth } from '../../context/AuthContext';
import { notifyError, notifySuccess } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TIERS_WITH_CALLS = new Set(['essentials', 'guided', 'connected', 'admin']);

const TIMELINE_OPTIONS = [
  'Less than 1 month',
  '1-3 months',
  '3-6 months',
  '6-12 months',
  '12+ months / planning',
];

const useIntakeForm = () => {
  const [form, setForm] = useState({ name: '', destination_city: '', timeline: '', top_questions: '' });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isValid = (
    form.name.trim().length >= 2 &&
    form.destination_city.trim().length >= 2 &&
    form.timeline &&
    form.top_questions.trim().length >= 5
  );
  return { form, set, isValid };
};

const IntakeForm = ({ form, set, isValid, busy, onSubmit, ctaLabel, testIdPrefix }) => (
  <div className="grid grid-cols-1 gap-4" data-testid={`${testIdPrefix}-form`}>
    <div>
      <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">Your name</label>
      <input
        type="text"
        value={form.name}
        onChange={(e) => set('name')(e.target.value)}
        placeholder="Jamie Rivera"
        className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2.5 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
        data-testid={`${testIdPrefix}-name`}
      />
    </div>
    <div>
      <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">Destination city</label>
      <input
        type="text"
        value={form.destination_city}
        onChange={(e) => set('destination_city')(e.target.value)}
        placeholder="Mexico City, Tulum, Mérida…"
        className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2.5 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
        data-testid={`${testIdPrefix}-city`}
      />
    </div>
    <div>
      <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">Timeline</label>
      <select
        value={form.timeline}
        onChange={(e) => set('timeline')(e.target.value)}
        className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2.5 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
        data-testid={`${testIdPrefix}-timeline`}
      >
        <option value="">Select…</option>
        {TIMELINE_OPTIONS.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">Top 3 questions for the call</label>
      <textarea
        value={form.top_questions}
        onChange={(e) => set('top_questions')(e.target.value)}
        rows={4}
        placeholder="1) Best neighborhoods for a family of 4&#10;2) Tax residency timeline&#10;3) Schooling options"
        className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2.5 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
        data-testid={`${testIdPrefix}-questions`}
      />
    </div>
    <button
      type="button"
      onClick={onSubmit}
      disabled={!isValid || busy}
      className="w-full bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      data-testid={`${testIdPrefix}-submit`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <>{ctaLabel} <ArrowRight className="w-4 h-4" /></>
      )}
    </button>
  </div>
);

const PortalScheduleCall = () => {
  const { user, loading } = useAuth();
  const [activeFlow, setActiveFlow] = useState(null);  // 'included' | 'paid'
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const intake = useIntakeForm();

  if (loading) {
    return (
      <PortalLayout activeTab="schedule-call">
        <div className="py-20 text-center text-[#0A1F2E]/55">Loading…</div>
      </PortalLayout>
    );
  }
  if (!user) return <Navigate to="/portal" replace />;

  const tier = (user.role || '').toLowerCase();
  const includedCallTier = TIERS_WITH_CALLS.has(tier);

  // Submit included call intake → open cal.com after successful save (per user spec).
  const submitIncluded = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/coaching/intake`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(intake.form),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) {
        notifySuccess('Intake saved - opening Cal.com booking…');
        setConfirmation({ kind: 'included', cal_url: d.cal_url || 'https://cal.com/mundalo' });
        window.open(d.cal_url || 'https://cal.com/mundalo', '_blank', 'noopener,noreferrer');
      } else {
        notifyError(d, 'Could not save intake.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Buy additional $99 call → save intake, redirect to Stripe checkout.
  const submitPaid = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/coaching/buy-additional-call`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intake: intake.form }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.url) {
        window.location.href = d.url;
      } else if (r.status === 503) {
        notifyError(d, 'The $99 call product isn\'t fully set up yet. Email hello@mundalo.com.');
      } else {
        notifyError(d, 'Could not start checkout.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalLayout activeTab="schedule-call">
      <SEO title="Schedule a Call - Mundalo" description="Book your 1:1 relocation strategy call." path="/portal/schedule-call" />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10" data-testid="portal-schedule-call-page">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2 inline-flex items-center gap-1.5">
            <Phone className="w-3 h-3" /> 1:1 Coaching
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Schedule a call
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            45-minute 1:1 with a Mundalo relocation specialist. We'll review your Blueprint, troubleshoot blockers,
            and map out the next 30 days. Fill in 4 quick fields first so we can be useful from minute one.
          </p>
        </header>

        {!activeFlow && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="schedule-call-options">
            {/* Option A: included call */}
            <article
              className={`bg-white border rounded-xl p-6 flex flex-col ${
                includedCallTier ? 'border-[#E8A830] shadow-lg' : 'border-[#EFE6CF] opacity-70'
              }`}
              data-testid="option-included-call"
            >
              <span className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-3 inline-flex items-center gap-1.5">
                <CheckCircle className="w-3 h-3" /> Included in your plan
              </span>
              <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Schedule your included call</h2>
              <p className="text-[#0A1F2E]/70 text-sm mb-5 flex-1">
                {includedCallTier ? (
                  <>You have 1 or more 45-min calls included with your current plan. Submit a brief intake form to get the most out of your session, then we'll open Cal.com to pick a time.</>
                ) : (
                  <>Coaching calls are included with Essentials, Guided, and Connected plans. Upgrade to unlock.</>
                )}
              </p>
              <button
                type="button"
                disabled={!includedCallTier}
                onClick={() => setActiveFlow('included')}
                className="w-full bg-[#E8A830] hover:bg-[#d59824] disabled:bg-[#F4ECD8] disabled:text-[#0A1F2E]/40 text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition inline-flex items-center justify-center gap-2"
                data-testid="option-included-cta"
              >
                <Calendar className="w-4 h-4" />
                {includedCallTier ? 'Continue' : 'Upgrade required'}
              </button>
            </article>

            {/* Option B: additional $99 call */}
            <article className="bg-white border border-[#EFE6CF] rounded-xl p-6 flex flex-col" data-testid="option-paid-call">
              <span className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-3 inline-flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Additional call
              </span>
              <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Buy an additional call - $99</h2>
              <p className="text-[#0A1F2E]/70 text-sm mb-5 flex-1">
                Already used your included call? Or want a second pass before your move? Pay $99 for another 45-min session.
              </p>
              <button
                type="button"
                onClick={() => setActiveFlow('paid')}
                className="w-full bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition inline-flex items-center justify-center gap-2"
                data-testid="option-paid-cta"
              >
                <ExternalLink className="w-4 h-4" /> Continue to checkout
              </button>
            </article>
          </div>
        )}

        {activeFlow && !confirmation && (
          <section className="bg-white border border-[#EFE6CF] rounded-xl p-6 md:p-8 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display italic text-2xl text-[#0A1F2E]">
                {activeFlow === 'included' ? 'Pre-call intake' : 'Pre-call intake - $99 additional call'}
              </h2>
              <button
                type="button"
                onClick={() => setActiveFlow(null)}
                className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] text-sm transition"
              >
                ← Back
              </button>
            </div>
            <p className="text-[#0A1F2E]/70 text-sm mb-6">
              Four quick fields. Takes under a minute. We'll send your answers to your coach so the call starts where you are.
            </p>
            <IntakeForm
              form={intake.form}
              set={intake.set}
              isValid={intake.isValid}
              busy={busy}
              onSubmit={activeFlow === 'included' ? submitIncluded : submitPaid}
              ctaLabel={activeFlow === 'included' ? 'Save and open Cal.com' : 'Save and pay $99'}
              testIdPrefix={`intake-${activeFlow}`}
            />
          </section>
        )}

        {confirmation?.kind === 'included' && (
          <>
            <GuidedUpsell
              variant="call-upsell"
              storageKey="schedule-call-after-included"
              onBuyAdditionalCall={() => {
                setConfirmation(null);
                setActiveFlow('paid');
              }}
            />
            <section className="bg-white border border-[#2CA8A0]/40 rounded-xl p-6 md:p-8 max-w-2xl" data-testid="schedule-call-confirmation">
            <CheckCircle className="w-10 h-10 text-[#2CA8A0] mb-4" />
            <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Intake saved.</h2>
            <p className="text-[#0A1F2E]/70 text-sm mb-5">
              Cal.com should have opened in a new tab. If your browser blocked the popup, use the button below.
            </p>
            <a
              href={confirmation.cal_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-3 rounded-full transition"
              data-testid="schedule-call-cal-link"
            >
              <Calendar className="w-4 h-4" /> Open Cal.com
            </a>
          </section>
          </>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalScheduleCall;
